import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { useVideos } from '../contexts/VideoContext';

interface VideoManagerProps {
  contentId: string;
  open: boolean;
  onClose: () => void;
}

const VideoManager: React.FC<VideoManagerProps> = ({ contentId, open, onClose }) => {
  const { videosByContentId, loadContentVideos, addVideoToContent, deleteVideoItem, updateVideoItem } = useVideos();
  const videos = videosByContentId[contentId] || [];

  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (open) {
      loadContentVideos(contentId);
    }
  }, [open, contentId, loadContentVideos]);

  const handleMoveVideo = async (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= videos.length) return;

    const currentVideo = videos[index];
    const targetVideo = videos[targetIndex];

    let currentIdxValue = currentVideo.order_index;
    let targetIdxValue = targetVideo.order_index;

    if (currentIdxValue === targetIdxValue) {
      currentIdxValue = index;
      targetIdxValue = targetIndex;
    }

    const temp = currentIdxValue;
    currentIdxValue = targetIdxValue;
    targetIdxValue = temp;

    try {
      const updatePromises = [
        updateVideoItem(currentVideo.id, { order_index: currentIdxValue }),
        updateVideoItem(targetVideo.id, { order_index: targetIdxValue })
      ];
      await Promise.all(updatePromises);
      await loadContentVideos(contentId);
    } catch (error) {
      console.error('Erro ao reordenar vídeos:', error);
    }
  };

  const handleAdd = async () => {
    if (!title.trim() || !url.trim()) return;
    const created = await addVideoToContent(contentId, {
      title: title.trim(),
      description: description.trim() || undefined,
      youtube_url: url.trim(),
    });
    if (created) {
      setTitle('');
      setUrl('');
      setDescription('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#1a1d23] border-[#1f2329] max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-white">Gerenciar Vídeos</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3">
            <Input
              placeholder="Título do vídeo"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-[#0f1114] border-[#1f2329] text-white"
            />
            <Input
              placeholder="URL do YouTube"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="bg-[#0f1114] border-[#1f2329] text-white"
            />
            <Textarea
              placeholder="Descrição (opcional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-[#0f1114] border-[#1f2329] text-white"
            />
            <Button onClick={handleAdd} className="bg-[#0FFF55] text-black hover:bg-[#0dd149]">Adicionar</Button>
          </div>

          <div className="max-h-72 overflow-y-auto divide-y divide-[#1f2329]">
            {videos.map((v, index) => (
              <div key={v.id} className="flex items-center gap-3 py-3">
                <img src={v.thumbnail_url} alt={v.title} className="w-20 h-12 object-cover rounded flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-semibold line-clamp-2">{v.title}</div>
                  <div className="text-gray-400 text-xs truncate">{v.youtube_url}</div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {index > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-8 h-8 p-0 border-gray-700 text-gray-400 hover:text-white"
                      onClick={() => handleMoveVideo(index, 'up')}
                      title="Mover para cima"
                    >
                      <ArrowUp className="w-3 h-3" />
                    </Button>
                  )}
                  {index < videos.length - 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-8 h-8 p-0 border-gray-700 text-gray-400 hover:text-white"
                      onClick={() => handleMoveVideo(index, 'down')}
                      title="Mover para baixo"
                    >
                      <ArrowDown className="w-3 h-3" />
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className="w-8 h-8 p-0 border-red-600 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    onClick={() => deleteVideoItem(v.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VideoManager;


